import Vue from 'vue'

const SET_CONFIGURATION_SETTINGS = 'SET_CONFIGURATION_SETTINGS'
const RESET_CONFIGURATION_SETTINGS = 'RESET_CONFIGURATION_SETTINGS'
const SET_COLUMN_DATA_VALIDATION_MODE = 'SET_COLUMN_DATA_VALIDATION_MODE'
const INSERT_COLUMN_NAME_ALIAS = 'UPSERT_COLUMN_NAME_ALIAS'
const INSERT_COLUMN_SKIP_VALUE = 'UPSERT_COLUMN_SKIP_VALUE'
const SET_DEFAULT_COLUMN_NAME_ALIAS = 'SET_DEFAULT_COLUMN_NAME_ALIAS'
const DELETE_COLUMN_NAME_ALIAS = 'DELETE_COLUMN_NAME_ALIAS'
const DELETE_COLUMN_SKIP_VALUE = 'DELETE_COLUMN_SKIP_VALUE'
const SET_COLUMN_METADATA = 'SET_COLUMN_METADATA'

const API_CONFIGURATIONS_ENDPOINT = (tenantId = '') => `Api/Configurations/${tenantId}`

const DataValidationModeProperty = 'DataValidationMode'
const NameAliasesProperty = 'NameAliases'
const SkipValueProperty = 'SkipValues'

let initialState = () => ({
  configurations: {
    Configurations: [],
  },
  // raw payload from API
  originalConfigurations: {
    Configurations: [],
  },
  isLoading: false,
  editMode: false,
})
/**
 * API Data: {
 *  TenantId: 1,
 *  Version: 1,
 *  Configurations: [
 *    {
 *      Name: 'Lab Data',
 *      ColumnConfigurations: [
 *        { ... }
 *      ]
 *    }
 *  ]
 * }
 * Store configuration state: {
 *  [ConfigurationName]: {
 *    [ColumnId]: { ... }
 *  }
 * }
 * @param {*} configurationSettings 
 */
const convertAPIDataToState = (configurationSettings) => {
  let configurationDictionary = {}
  configurationSettings.Configurations.forEach(config => {
    if (configurationDictionary[config.Name] != null) {
      return
    }
    configurationDictionary[config.Name] = {}
    config.ColumnConfigurations.forEach(column => {
      let columnConfigurationDictionary = configurationDictionary[config.Name]
      if (columnConfigurationDictionary[column.Id] != null) {
        return
      }
      columnConfigurationDictionary[column.Id] = column
    })
  })
  return configurationDictionary
}

const convertSingleSettingStateToAPIData = (stateConfigurations, configName) => {
  if ((stateConfigurations && stateConfigurations[configName]) == null) {
    return null
  }
  return {
    Name: configName,
    ColumnConfigurations: Object.values(stateConfigurations[configName]),
  }
}

const convertStateToApiData = (stateConfigurations) => {
  let payload = {
    Configurations: [],
  }
  payload.Configurations = Object.keys(stateConfigurations).map(configName =>
    convertSingleSettingStateToAPIData(stateConfigurations, configName)
  )
  return payload
}

const pluginServiceLink = 'hatfield_edd_ingestion'
const getProxyUrlMaker = (rootGetters, subpath) => {
  let plugin = rootGetters['plugin/activePluginByServiceLink'](pluginServiceLink)
  return plugin.service.proxyUrlMaker(subpath)
}

export default {
  namespaced: true,
  state: initialState,
  actions: {
    async fetchConfigurationSettings({
      commit,
      rootState,
      rootGetters,
    }) {
      const tenantId = rootState.authenticate && rootState.authenticate.userinfo && rootState.authenticate.userinfo.roleMapId
      const url = getProxyUrlMaker(rootGetters, API_CONFIGURATIONS_ENDPOINT(tenantId))
      const res = await Vue.axios.get(url)
      commit(SET_CONFIGURATION_SETTINGS, res.data[0])
    },
    setConfigurationSettings({
      commit,
    }, configurationSettings) {
      commit(SET_CONFIGURATION_SETTINGS, configurationSettings)
    },
    resetConfigurationSettings({
      commit,
    }) {
      commit(RESET_CONFIGURATION_SETTINGS)
    },
    async submitConfigurationSettings({
      commit,
      state,
      rootState,
      rootGetters,
    }) {
      // TODO      
      const configurationEntries = state.configurations
      const configPayload = convertStateToApiData(configurationEntries)
      const tenantId = rootState.authenticate && rootState.authenticate.userinfo && rootState.authenticate.userinfo.roleMapId
      const url = getProxyUrlMaker(rootGetters, API_CONFIGURATIONS_ENDPOINT(tenantId))
      const res = await Vue.axios.put(url, configPayload)
      commit(SET_CONFIGURATION_SETTINGS, res.data[0])
    },
    setColumnDataValidationMode({
      commit,
    }, {
      configName,
      columnId,
      dataValidationMode,
    }) {
      commit(SET_COLUMN_DATA_VALIDATION_MODE, {
        configName,
        columnId,
        dataValidationMode,
      })
    },
    insertSkipValue({
      commit,
    }, {
      configName,
      columnId,
      skipValue,
    }) {
      commit(INSERT_COLUMN_SKIP_VALUE, {
        configName,
        columnId,
        skipValue,
      })
    },
    deleteColumnSkipValue({
      commit,
    }, {
      configName,
      columnId,
      skipValue,
    }) {
      commit(DELETE_COLUMN_SKIP_VALUE, {
        configName,
        columnId,
        skipValue,
      })
    },
    upsertColumnNameAlias({
      commit,
    }, {
      configName,
      columnId,
      aliasName,
      isDefault,
    }) {
      commit(INSERT_COLUMN_NAME_ALIAS, {
        configName,
        columnId,
        aliasName,
      })
      if (isDefault) {
        commit(SET_DEFAULT_COLUMN_NAME_ALIAS, {
          configName,
          columnId,
          aliasName,
        })
      }
    },
    deleteColumnAlias({
      commit,
    }, {
      configName,
      columnId,
      aliasName,
    }) {
      commit(DELETE_COLUMN_NAME_ALIAS, {
        configName,
        columnId,
        aliasName,
      })
    },
    setColumnMetadata({
      commit,
    }, {
      configName,
      columnId,
      metadataKey,
      metadataValue,
    }) {
      commit(SET_COLUMN_METADATA, {
        configName,
        columnId,
        metadataKey,
        metadataValue,
      })
    },
  },
  mutations: {
    [SET_CONFIGURATION_SETTINGS](state, configuration) {
      state.originalConfigurations = configuration
      state.configurations = convertAPIDataToState(configuration)
    },
    [RESET_CONFIGURATION_SETTINGS](state) {
      state.configurationSettings = convertAPIDataToState(state.originalConfigurations)
    },
    [SET_COLUMN_DATA_VALIDATION_MODE](state, {
      configName,
      columnId,
      dataValidationMode,
    }) {
      if (configName == null || columnId == null || dataValidationMode == null) {
        return
      }
      let columnConfiguration = state.configurations && state.configurations[configName] && state.configurations[configName][columnId]
      if (columnConfiguration == null) {
        return
      }
      const CanConfigureColumn = columnConfiguration.CanConfigureDataValidationMode
      if (!CanConfigureColumn) {
        return
      }
      Vue.set(columnConfiguration, DataValidationModeProperty, dataValidationMode)
    },
    [INSERT_COLUMN_SKIP_VALUE](state, {
      configName,
      columnId,
      skipValue,
    }) {
      if (configName == null || columnId == null || skipValue == null) {
        return
      }
      let columnConfiguration = state.configurations && state.configurations[configName] && state.configurations[configName][columnId]
      if (columnConfiguration == null) {
        return
      }
      if (columnConfiguration.SkipValues == null) {
        columnConfiguration.SkipValues = []
      }
      const nameSkipValueAlreadyExists = columnConfiguration.SkipValues.includes(skipValue)
      if (nameSkipValueAlreadyExists) {
        return
      }
      let skipValueToBeAdded = skipValue
      // Vue.set(columnConfiguration, SkipValueProperty, [...columnConfiguration.SkipValues, skipValueToBeAdded])
      columnConfiguration.SkipValues.push(skipValueToBeAdded)
    },
    [DELETE_COLUMN_SKIP_VALUE](state, {
      configName,
      columnId,
      skipValue,
    }) {
      if (configName == null || columnId == null || skipValue == null) {
        return
      }
      let columnConfiguration = state.configurations && state.configurations[configName] && state.configurations[configName][columnId]
      if (columnConfiguration == null) {
        return
      }
      const skipValueIndex = columnConfiguration.SkipValues.findIndex(x => x == skipValue)
      if (skipValueIndex < 0) {
        return
      }
      let splicedSkipValues = [
        ...columnConfiguration.SkipValues.slice(0, skipValueIndex),
        ...columnConfiguration.SkipValues.slice(skipValueIndex + 1, columnConfiguration.SkipValues.length),
      ]
      Vue.set(columnConfiguration, SkipValueProperty, splicedSkipValues)
    },
    [INSERT_COLUMN_NAME_ALIAS](state, {
      configName,
      columnId,
      aliasName,
    }) {
      if (configName == null || columnId == null || aliasName == null) {
        return
      }
      let columnConfiguration = state.configurations && state.configurations[configName] && state.configurations[configName][columnId]
      if (columnConfiguration == null) {
        return
      }
      if (columnConfiguration.NameAliases == null) {
        columnConfiguration.NameAliases = []
      }
      const nameAliasAlreadyExists = columnConfiguration.NameAliases.findIndex(x => x.Name == aliasName) > 0
      if (nameAliasAlreadyExists) {
        return
      }
      let aliasToBeAdded = {
        Name: aliasName,
        IsDefault: false,
      }
      Vue.set(columnConfiguration, NameAliasesProperty, [...columnConfiguration.NameAliases, aliasToBeAdded])

    },
    [SET_DEFAULT_COLUMN_NAME_ALIAS](state, {
      configName,
      columnId,
      aliasName,
    }) {
      if (configName == null || columnId == null || aliasName == null) {
        return
      }
      let columnConfiguration = state.configurations && state.configurations[configName] && state.configurations[configName][columnId]
      if (columnConfiguration == null) {
        return
      }
      let alias = columnConfiguration.NameAliases.find(x => x.Name == aliasName)
      if (alias == null) {
        return
      }
      alias.IsDefault = true
      let updatedColumnNameAliases = [
        alias,
        ...columnConfiguration.NameAliases
        .filter(x => x.Name != aliasName)
        .map(x => {
          x.IsDefault = false
          return x
        }),
      ]
      console.log('set default', updatedColumnNameAliases)
      Vue.set(columnConfiguration, NameAliasesProperty, updatedColumnNameAliases)
    },
    [DELETE_COLUMN_NAME_ALIAS](state, {
      configName,
      columnId,
      aliasName,
    }) {
      if (configName == null || columnId == null || aliasName == null) {
        return
      }
      let columnConfiguration = state.configurations && state.configurations[configName] && state.configurations[configName][columnId]
      if (columnConfiguration == null) {
        return
      }
      const aliasIndex = columnConfiguration.NameAliases.findIndex(x => x.Name == aliasName)
      if (aliasIndex < 0) {
        return
      }
      let splicedNameAliases = [
        ...columnConfiguration.NameAliases.slice(0, aliasIndex),
        ...columnConfiguration.NameAliases.slice(aliasIndex + 1, columnConfiguration.NameAliases.length),
      ]
      Vue.set(columnConfiguration, NameAliasesProperty, splicedNameAliases)
    },
    [SET_COLUMN_METADATA](state, {
      configName,
      columnId,
      metadataKey,
      metadataValue,
    }) {
      if (configName == null || columnId == null || metadataKey == null) {
        return
      }
      let columnConfiguration = state.configurations && state.configurations[configName] && state.configurations[configName][columnId]
      if (columnConfiguration == null) {
        return
      }
      if (columnConfiguration.Metadata == null) {
        columnConfiguration.Metadata = {}
      }
      Vue.set(columnConfiguration.Metadata, metadataKey, metadataValue)
    },
  },
  getters: {
    findConfigurationEntry: (state) => (configName) => {
      if (configName == null) {
        return null
      }
      return state.configurations[configName]
    },
    findColumnConfiguration: (state) => (configName, columnId) => {
      if (configName == null || columnId == null) {
        return null
      }
      return state.configurations && state.configurations[configName] && state.configurations[configName][columnId]
    },
    findConfigurationEntryInOriginal: (state) => (configName) => {
      if (configName == null) {
        return null
      }
      return state.originalConfigurations.Configurations.find(x => x.Name == configName)
    },
    originalConfigurations: (state) => state.originalConfigurations.Configurations,
    configurations: (state) => state.configurations,
  },
}
export {
  DataValidationModeProperty,
}